import { Divider, FormControlLabel, Grid, Switch } from '@mui/material';
import { GridColumn } from '@progress/kendo-react-grid';
import { Form, Formik, FormikProps } from 'formik';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { GradeDto } from 'src/store/grades/grade';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import {
  RunSystemDetailDto,
  RunSystemGradeDto
} from 'src/store/runsystems/runsystem';
import { selectRunSystemArhiving } from 'src/store/runsystems/runsystems.selectors';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  runSystem: Nullable<RunSystemDetailDto>;
  onCancel: () => void;
  onSubmit: (runSystem: RunSystemDetailDto) => void;
};

export const RunSystemForm = forwardRef<FormikProps<RunSystemDetailDto>, Props>(
  (props, ref) => {
    const { onSubmit, onCancel } = props;

    const [runSystem, setRunSystem] = useState<RunSystemDetailDto>(
      props.runSystem ||
        ({
          active: true,
          productsRemixable: false,
          mixRunSystem: false
        } as RunSystemDetailDto)
    );

    const [grades, setGrades] = useState<GradeDto[]>([]);

    const { t } = useTranslation();

    const grades$ = useSelector(selectActiveGrades);
    const runSystemArchiving$ = useSelector(selectRunSystemArhiving);

    const validate = (runSystem: RunSystemDetailDto) => {
      const validationRunners = [
        validationRunner('no', t('props.number'), required),
        validationRunner('name', t('props.name'), required, maxLength(50)),
        validationRunner('description', t('props.description'), maxLength(200))
      ];
      return runValidations(runSystem, validationRunners);
    };

    useEffect(() => {
      setGrades(
        grades$.filter(
          grade =>
            !runSystem.runSystemGrades?.some(rsg => rsg.gradeId === grade.id)
        )
      );
    }, [runSystem.runSystemGrades, grades$]);

    const onAddGrade = (
      runSystem: RunSystemDetailDto,
      grade: GradeDto,
      setFieldValue: any
    ) => {
      const runSystemGrades = [
        ...(runSystem.runSystemGrades ?? []),
        {
          runSystemId: runSystem.id,
          runSystemNo: +runSystem.no,
          runSystemName: runSystem.name,
          gradeId: grade.id,
          gradeNo: +grade.no,
          gradeName: grade.name
        } as RunSystemGradeDto
      ];
      setRunSystem({ ...runSystem, runSystemGrades: runSystemGrades });
      setFieldValue('runSystemGrades', runSystemGrades);
    };

    const onRemoveGrade = (
      runSystem: RunSystemDetailDto,
      runSystemGrade: RunSystemGradeDto,
      setFieldValue: any
    ) => {
      const runSystemGrades = runSystem.runSystemGrades.filter(
        rsg => rsg.gradeId !== runSystemGrade.gradeId
      );
      setRunSystem({ ...runSystem, runSystemGrades: runSystemGrades });
      setFieldValue('runSystemGrades', runSystemGrades);
    };

    return (
      <Formik
        initialValues={runSystem}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <div className="flex flexcol">
              <Grid container>
                <Grid item xs={12} lg={3}>
                  <InputField
                    name="no"
                    type="number"
                    label={t('props.number')}
                    value={values.no}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={errors.no}
                    displayError={touched.no || submitCount > 0}
                  />
                </Grid>
                <Grid item xs={12} lg={9}>
                  <InputField
                    name="name"
                    label={t('props.name')}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={errors.name}
                    displayError={touched.name || submitCount > 0}
                  />
                </Grid>
              </Grid>
              <InputField
                name="description"
                label={t('props.description')}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={errors.description}
                displayError={touched.description || submitCount > 0}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={handleChange}
                    name="active"
                  />
                }
                label={t('props.active')}
              />
              <Checkbox
                label={t('runSystem.productsRemixable')}
                defaultChecked={values.productsRemixable}
                name="productsRemixable"
                onChange={handleChange}
              />
              <Checkbox
                className="mb-32"
                label={t('runSystem.mixRunSystem')}
                defaultChecked={values.mixRunSystem}
                name="mixRunSystem"
                onChange={handleChange}
              />
              <FormField>
                <Autocomplete
                  options={runSystemArchiving$}
                  label={t('runSystem.archiving')}
                  optionField="name"
                  value={values.runSystemArchiving}
                  onChange={runSystemArchiving =>
                    setFieldValue('archivingId', runSystemArchiving?.id)
                  }
                  onBlur={handleBlur}
                />
              </FormField>
            </div>

            <Divider className="mb-32" />

            <Grid container>
              <Grid item xs={6}>
                <DataGrid
                  items={values.runSystemGrades}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'gradeName', dir: 'asc' }]}
                >
                  <GridColumn
                    field="gradeName"
                    title={t('grade.plural')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.gradeName}
                          </BodyType>
                          <ActionButton
                            icon="la la-times"
                            onClick={() =>
                              onRemoveGrade(
                                values,
                                props.dataItem,
                                setFieldValue
                              )
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>

              <Grid item xs={6}>
                <DataGrid
                  items={grades}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'name', dir: 'asc' }]}
                >
                  <GridColumn
                    field="name"
                    title={t('grade.all')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.name}
                          </BodyType>
                          <ActionButton
                            icon="la la-plus"
                            onClick={() =>
                              onAddGrade(values, props.dataItem, setFieldValue)
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>
            </Grid>

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
